<script lang="ts">
  import { format, parse } from 'date-fns';
  import { getContext, onMount } from 'svelte';
  import { field, form } from 'svelte-forms';
  import { required } from 'svelte-forms/validators';
  import { location, push } from 'svelte-spa-router';
  import { prefectures } from '../../../components/Prefecture.js';
  import Loading from '../../../etc/loader.svelte';
  import InputInvitationCodeModal from '../../../etc/modal/invitation/umobiled/InputInvitationCodeModal.svelte';
  import {
    RegisterInvitationTransferApplicationResult,
    VerifyAccessTokenResult,
    VerifyInvitationCodeResult
  } from '../../../repositories/invitationumobiled';
  import RepositoryFactory, { INVITATION_UMOBILED } from '../../../repositories/RepositoryFactory';
  import NotFound from './NotFound.svelte';
  import { dataPlanCodes, getPlanCodeLabel, voicePlanCodes } from './types';
  import {
    isValidBirthDate,
    isValidMnpExpiration,
    isValidMnpNo,
    isValidMnpTelNo,
    isValidTelNo,
    isValidZipcode,
    isZenkaku,
    isZenkakuKana
  } from './Validator';

  const { open, close }: any = getContext('simple-modal');

  const repository = RepositoryFactory[INVITATION_UMOBILED];

  type TDisplay = 'AGREEMENT' | 'DETAILS' | 'CONFIRM' | 'ERROR';
  let display: TDisplay = 'AGREEMENT';
  const transitionDisplay = (nextDisplay: TDisplay) => {
    display = nextDisplay;
  };
  let isLoading = false;

  /* 同意確認 初期表示 */
  $: $location;
  const accessToken = $location.replace('/invitation/umobiled/main/', '');
  $: verifyAccessTokenResult = null;

  onMount(() => {
    if (display === 'AGREEMENT') {
      if (!accessToken) {
        transitionDisplay('ERROR');
      }
      verifyAccessTokenAsOnMount();
    }
  });

  const verifyAccessTokenAsOnMount = async () => {
    await repository
      .verifyAccessToken({
        accessToken: accessToken
      })
      .then((response: VerifyAccessTokenResult) => {
        verifyAccessTokenResult = response;
      })
      .catch((error) => {});

    if (!verifyAccessTokenResult?.isValidToken) {
      transitionDisplay('ERROR');
      return;
    }
  };

  /* 同意確認〜招待コード入力まで */
  type ConfirmedBgColor = 'bg-gray-100' | 'bg-sky-100';

  let agreementFlg: boolean = false;
  let confirmedBgColor: ConfirmedBgColor = 'bg-gray-100';

  $: verifyInvitationCodeResult = null;

  const onChangeCheckbox = (): void => {
    confirmedBgColor = agreementFlg ? 'bg-sky-100' : 'bg-gray-100';
  };

  $: isAgreementChecked = () => {
    return agreementFlg;
  };

  const showInvitationCodeModal = () => {
    open(
      InputInvitationCodeModal,
      {
        handleCloseEvent: (result: VerifyInvitationCodeResult) => {
          if (result) {
            verifyInvitationCodeResult = result;
            transitionDisplay('DETAILS');
          } else {
            transitionDisplay('ERROR');
          }
          close();
        }
      },
      {
        closeButton: true,
        closeOnEsc: false,
        closeOnOuterClick: false
      }
    );
  };

  /* 申込入力〜確認まで */
  $: isVoicePlan = verifyInvitationCodeResult?.planTypeCode === 'VOICE';
  $: planCodes = isVoicePlan ? voicePlanCodes : dataPlanCodes;

  $: plancode = planCodes[0].value;
  const mnpTelno = field('mnpTelno', '', [required(), isValidMnpTelNo]);
  const mnpReserveNo = field('mnpReserveNo', '', [required(), isValidMnpNo]);
  const mnpExpiration = field('mnpExpiration', '', [required(), isValidMnpExpiration]);

  const sei = field('sei', '', [required(), isZenkaku]);
  const mei = field('mei', '', [required(), isZenkaku]);
  const seiKana = field('seiKana', '', [required(), isZenkakuKana]);
  const meiKana = field('meiKana', '', [required(), isZenkakuKana]);

  const sex = field('sex', '', [required()]);

  const birthDate = field('birthDate', '', [required(), isValidBirthDate]);

  const zipcode = field('zipcode', '', [required(), isValidZipcode]);
  const prefectureCode = field('prefectureCode', '', [required()]);
  const city = field('city', '', [required(), isZenkaku]);
  const street = field('street', '', [required(), isZenkaku]);
  const building = field('building', '', [isZenkaku]);
  const telno = field('telno', '', [required(), isValidTelNo]);
  const myForm = form(
    mnpTelno,
    mnpReserveNo,
    mnpExpiration,
    sei,
    mei,
    seiKana,
    meiKana,
    sex,
    birthDate,
    zipcode,
    prefectureCode,
    city,
    street,
    building,
    telno
  );

  const handleConfirm = async () => {
    let isFormValid = false;

    await myForm.validate().then(() => {
      if (isVoicePlan) {
        isFormValid = $myForm.valid;
      } else {
        // データプランの場合はMNP関連の入力項目は非表示のため、必ず必須エラーとなる。エラーがあっても無視するように
        // 入力項目をsvelte-formsでのバリデーションを行わないようにするよりもこちらの方法の方が簡潔にできたためこの方法にしています
        isFormValid = $myForm.valid || hasMnpValidateRequiredError;
      }
    });

    if (!isFormValid) {
      return;
    }

    transitionDisplay('CONFIRM');
  };

  $: hasMnpValidateRequiredError =
    $myForm.errors.length == 3 &&
    ($myForm.hasError('mnpTelno.required') ||
      $myForm.hasError('mnpReserveNo.required') ||
      $myForm.hasError('mnpExpiration.required'));

  /* 申込確認 */
  const getPrefectureName = (code: string) => {
    const prefecture = prefectures.find((p) => p.id === code);
    return prefecture ? prefecture.name : '';
  };

  const getSexName = (code: string) => {
    switch (code) {
      case 'MALE':
        return '男性';
      case 'FEMALE':
        return '女性';
      default:
        return '';
    }
  };

  const formatBirthDate = (date: string) => {
    if (!date) {
      return '';
    }
    const parsedDate = parse(date, 'yyyyMMdd', new Date());
    return format(parsedDate, 'yyyy/MM/dd');
  };

  const formatApplyMnpExpiration = (date: string) => {
    if (!date) {
      return '';
    }
    const parsedDate = parse(date, 'MM/dd', new Date());
    return format(parsedDate, 'yyyy-MM-dd');
  };

  const formatApplyBirthDate = (date: string) => {
    const parsedDate = parse(date, 'yyyyMMdd', new Date());
    return format(parsedDate, 'yyyy-MM-dd');
  };

  let applyErrroMessage: string | null = null;

  const handleApply = async () => {
    applyErrroMessage = null;
    isLoading = true;
    repository
      .registerInvitationTransferApplication({
        accessToken: accessToken,
        invitationCode: verifyInvitationCodeResult.invitationCode,

        mstServiceCode: plancode,

        keyMsisdn: $mnpTelno.value || null,
        mnpNumber: $mnpReserveNo.value || null,
        mnpExpirationDate: formatApplyMnpExpiration($mnpExpiration.value) || null,

        customerSei: $sei.value,
        customerMei: $mei.value,
        customerSeiKana: $seiKana.value,
        customerMeiKana: $meiKana.value,
        sex: $sex.value,
        birthDate: formatApplyBirthDate($birthDate.value),
        zipcode: $zipcode.value,
        prefectureCode: $prefectureCode.value,
        city: $city.value,
        street: $street.value,
        building: $building.value || null,
        telNo: $telno.value,
        email: verifyAccessTokenResult.email
      })
      .then((response: RegisterInvitationTransferApplicationResult) => {
        isLoading = false;
        if (response) {
          push(`/invitation/umobiled/received/${response.accessToken}/${response.invitationCode}/`);
        } else {
          applyErrroMessage = '申込に失敗しました。時間をおいて再度お試しください';
        }
      })
      .catch((error) => {
        isLoading = false;
        applyErrroMessage = '予期せぬエラーが発生しました。時間をおいて再度お試しください';
      });
  };

  const handleReturnToInput = async () => {
    applyErrroMessage = null;
    transitionDisplay('DETAILS');
  };
</script>

{#if isLoading}
  <Loading />
{/if}

{#if display === 'AGREEMENT'}
  <div class="h-full flex flex-col justify-between items-center login-background">
    <header class="container top-0 z-0">
      <div class="justify-between items-center" style="height:56px">
        <img style="height:36px" class="object-none w-full" src="/image/unext_communication_logo.svg" alt="logo" />
      </div>
    </header>

    <main class="container mt-10 mb-auto">
      <div class="flex justify-center text-2xl">U-mobile(d)移管特別オファー申込ページ</div>

      <div class="flex justify-center mt-5">
        <div class="m-5">
          <div class="inline-flex justify-items-center min-w-full">
            <div class="mb:font-sm w-full">
              <ul class="list-none">
                <li>
                  <p>U-mobile MAX/U-mobile Sをご利用いただいていた方向けの申込ページへようこそ。</p>
                </li>
                <li>
                  <p>下記お申込みの流れをご確認の上、申込手続きにお進みください。</p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-5">
        <div class="shadow-sm p-5 warning-background">
          <div class="content-help-title-xl">お申込み～開通までの流れ</div>

          <div class="mt-5">
            <p class="content-help-title inline-flex">
              <span>1. お申込みの流れの確認、規約等への同意</span>
            </p>
            <ul class="list-none content-help-text">
              <li>本ページ記載の流れ、規約等をご確認いただき、同意ボタンを押してください</li>
            </ul>
          </div>

          <div class="mt-2">
            <p class="content-help-title inline-flex">
              <span>2.お申込み情報の登録</span>
            </p>
            <ul class="list-none content-help-text">
              <li>メールにてお送りした招待コード、お申込みプラン、ご契約者情報を登録ください</li>
            </ul>
          </div>

          <div class="mt-2">
            <p class="content-help-title inline-flex">
              <span>3.決済情報（クレジットカード)の登録</span>
            </p>
            <ul class="list-none content-help-text">
              <li>申込情報登録後、決済情報を登録していただくことで申込が完了します</li>
            </ul>
          </div>

          <div class="mt-2">
            <p class="content-help-title">
              <span>4.SIMカードの発送</span>
            </p>
            <ul class="list-none content-help-text">
              <li>申込が完了するとSIMカードをご契約者さま宛に送付させていただきます</li>
              <li>配送情報をメールにて送付いたしますので配送状況のご確認にご利用ください</li>
            </ul>
          </div>

          <div class="grid grid-cols-2 gap-x-4 text-base mt-4">
            <div class="grid-1">
              <p class="content-help-title">
                <span>(音声プラン※の方)</span>
              </p>

              <div class="mt-1">
                <p class="content-help-title">
                  <span>5.SIMカードの受取、開通手続き</span>
                </p>
                <ul class="list-none content-help-text">
                  <li>
                    SIMが到着しましたら、開通手続きページ（URLは配送情報記載のメールを参照ください）より開通手続きを行ってください
                  </li>
                </ul>
              </div>

              <div class="mt-2">
                <p class="content-help-title">
                  <span>6.開通手続き完了メール受信</span>
                </p>
                <ul class="list-none content-help-text">
                  <li>開通手続き完了後、当社より手続き完了メールを送付します</li>
                  <li>メールを受信したら、お手持ちのスマホにSIMを指し、APN設定を行ってください</li>
                </ul>
              </div>

              <div class="mt-2">
                <p class="content-help-title mt-2">
                  <span>7.ご利用開始</span>
                </p>
                <ul class="list-none content-help-text">
                  <li>APN設定が完了すれば利用開始となります。</li>
                </ul>
              </div>
            </div>

            <div class="grid-1">
              <p class="content-help-title">
                <span>(データプランの方)</span>
              </p>

              <div class="mt-1">
                <p class="content-help-title">
                  <span>5.SIMカードの受取</span>
                </p>
                <ul class="list-none content-help-text">
                  <li>SIMが到着しましたら、お手持ちのスマホにSIMを指し、APN設定を行ってください</li>
                </ul>
              </div>

              <div class="mt-2">
                <p class="content-help-title items-center mt-2">
                  <span>6.ご利用開始</span>
                </p>
                <ul class="list-none content-help-text">
                  <li>APN設定が完了すれば利用開始となります。</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="justify-center p-4">
            <div class="border-4 border-red-500 text-red-500 p-4" role="alert">
              <p class="text-lg font-semibold">
                <span>音声プランをお申込みする場合の注意事項</span>
              </p>

              <p class="text-sm font-semibold">
                申込完了後、SIMが発送されると、お客さまにて開通手続きがされない場合一定期間経過後(MNP予約番号の有効期限前日)に弊社で自動的に開通処理をさせていただきます
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-10">
        <div class="shadow-sm p-5 warning-background">
          <div class="content-help-title-xl">お申込みにあたってご同意いただきたいこと</div>

          <div class="mt-5">
            <p class="content-help-title mt-2">
              <span>通信について</span>
            </p>
            <ul class="list-disc list-outside content-help-text ml-4">
              <li>
                パケット通信のご利用にあたり、ネットワークの混雑時における品質確保を目的として、画面の表示速度や動画の再生開始時間を早くするための通信の最適化を実施する場合があります。通信の最適化とは、動画をダウンロードするペースの調整などのトラフィック制御のことをいいます
              </li>
            </ul>
          </div>

          <div class="mt-5">
            <p class="content-help-title mt-2">
              <span>本プランにおける注意事項</span>
            </p>
            <ul class="list-disc list-outside content-help-text space-y-1 ml-4">
              <li>
                本プランは、U-mobile MAX/U-mobile
                Sをご契約いただいていた方のみがお申込みいただけます。別の名義ではお申込みいただけません
              </li>
              <li>第三者のお申込みであることが発覚した場合、SIMの開通後であっても解約となる場合があります</li>
              <li>「U-mobile MAX 通話プラス」をご契約いただいていた方は、本プランへのお申込みはMNPに限定されます</li>
              <li>本プランから、本サービスの他のプランへプラン変更することは出来ません</li>
              <li>その他、本プランについて定めた個別規定があります。必ずご確認ください</li>
            </ul>
          </div>

          <div class="mt-5">
            <p class="content-help-title mt-2">
              <span>利用規約</span>
            </p>
            <ul class="list-disc list-outside content-help-text space-y-1 ml-4">
              <li>以下の利用規約をご確認の上、ご同意ください</li>
              <ul class="list-disc list-outside content-help-text space-y-1 ml-5">
                <li>
                  <a
                    href="https://docs.unext.jp/pdf/umobile/service.pdf"
                    class="underline"
                    rel="noopener noreferrer"
                    target="_blank">U-mobile 通話プラス利用規約</a
                  >
                </li>
                <li>
                  <a
                    href="https://docs.unext.jp/pdf/umobile/importance.pdf"
                    class="underline"
                    rel="noopener noreferrer"
                    target="_blank">U-mobile 通話プラス重要説明事項</a
                  >
                </li>
                <li>
                  <a
                    href="https://docs.unext.jp/pdf/umobile/d-service.pdf"
                    class="underline"
                    rel="noopener noreferrer"
                    target="_blank">U-mobile データ専用利用規約</a
                  >
                </li>
                <li>
                  <a
                    href="https://docs.unext.jp/pdf/umobile/d-importance.pdf"
                    class="underline"
                    rel="noopener noreferrer"
                    target="_blank">U-mobile データ専用重要説明事項</a
                  >
                </li>
                <li>
                  <a
                    href="https://docs.unext.jp/pdf/umobile/dedicated_plan.pdf"
                    class="underline"
                    rel="noopener noreferrer"
                    target="_blank">U-mobile 専用プラン個別規定</a
                  >
                </li>
              </ul>
            </ul>
          </div>
          <div class="mt-5">
            <p class="content-help-title mt-2">
              <span>フィルタリングについて</span>
            </p>
            <ul class="list-none">
              <li>
                <p class="content-help-text">
                  保護者が契約者となり18歳未満の方が利用する場合、フィルタリングサービスの利用が法律上義務付けられています。ご契約者様にてマイページよりお申込みください
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="flex flex-col justify-center items-center p-2 mt-10">
        <div class="basis-2/3">
          <div class="inline-flex items-center px-4 rounded border border-transparent {confirmedBgColor}">
            <input
              id="agreementFlg"
              type="checkbox"
              class="w-4 h-4 text-red-600"
              bind:checked={agreementFlg}
              on:change={onChangeCheckbox}
            />
            <label for="agreementFlg" class="py-4 ml-2 w-full text-lg font-semibold text-gray-900"
              >上記手続きの流れ、規約等の同意事項を確認し申込に進む</label
            >
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-center items-center mt-2 mb-10">
        <button
          class="tracking-widest form-button h-14 w-1/5"
          disabled={!isAgreementChecked()}
          on:click={showInvitationCodeModal}>次へ</button
        >
      </div>
    </main>

    <footer class="container" />
  </div>
{:else if display === 'DETAILS'}
  <div class="flex flex-col justify-between items-center login-background">
    <header class="container top-0 z-0">
      <div class="justify-between items-center" style="height:56px">
        <img style="height:36px" class="object-none w-full" src="/image/unext_communication_logo.svg" alt="logo" />
      </div>
    </header>

    <main class="container mt-10 mb-auto">
      <div class="flex justify-center text-2xl">U-mobile(d)移管特別オファー申込ページ</div>

      <form>
        <div class="flex justify-center mt-10">
          <div class="content-center shadow-sm p-5 form-background">
            <!-- 申込プラン -->
            <div class="flex-1 justify-start">
              <label for="plancode" class="form-input-label required">申込プラン</label>
              <select
                id="plancode"
                name="plancode"
                class="p-3 mt-1 appearance"
                value={plancode}
                on:change={(e) => (plancode = e.currentTarget.value)}
              >
                {#each planCodes as plan}
                  <option value={plan.value}>{plan.label}</option>
                {/each}
              </select>
            </div>

            {#if verifyInvitationCodeResult?.planTypeCode === 'VOICE'}
              <!-- MNP電話番号 -->

              <div class="flex-1 justify-start mt-4">
                <label for="mnpTelno" class="form-input-label required">MNP電話番号（ハイフンなし）</label>
                <div class="flex flex-row justify-start space-x-4">
                  <div class="basis-1/2">
                    <input
                      type="text"
                      id="mnpTelno"
                      maxlength="14"
                      autocomplete="off"
                      placeholder="例) 08011112222"
                      bind:value={$mnpTelno.value}
                    />
                    {#if $myForm.hasError('mnpTelno.required')}
                      <p class="helper-text">MNP電話番号を入力してください</p>
                    {/if}
                    {#if $myForm.hasError('mnpTelno.invalid_mnp_tel_no')}
                      <p class="helper-text">MNP電話番号が間違えています。正しい電話番号を入力ください</p>
                    {/if}
                  </div>
                  <div class="basis-1/2"></div>
                </div>
              </div>

              <!-- MNP予約番号 / 有効期限 -->
              <div class="flex-1 justify-start mt-4">
                <label for="mnpReserveNo" class="form-input-label required">MNP予約番号 / 有効期限</label>
                <div class="flex flex-row justify-start space-x-4">
                  <div class="basis-1/2">
                    <input
                      type="text"
                      id="mnpReserveNo"
                      maxlength="10"
                      autocomplete="off"
                      placeholder="半角数字"
                      bind:value={$mnpReserveNo.value}
                    />
                    {#if $myForm.hasError('mnpReserveNo.required')}
                      <p class="helper-text">MNP予約番号を入力してください</p>
                    {/if}
                    {#if $myForm.hasError('mnpReserveNo.invalid_mnp_no')}
                      <p class="helper-text">MNP予約番号が間違えています。正しい予約番号を入力ください</p>
                    {/if}
                  </div>
                  <div class="basis-1/2">
                    <input
                      type="text"
                      id="mnpExpiration"
                      maxlength="5"
                      autocomplete="off"
                      placeholder="MM/DD"
                      bind:value={$mnpExpiration.value}
                    />
                    {#if $myForm.hasError('mnpExpiration.required')}
                      <p class="helper-text">MNP有効期限を入力してください</p>
                    {/if}
                    {#if $myForm.hasError('mnpExpiration.invalid_mnp_expiration')}
                      <p class="helper-text">MNP有効期限に正しい日付を入力ください</p>
                    {/if}
                    {#if $myForm.hasError('mnpExpiration.mnp_expiration_not_more_than_ten_days')}
                      <p class="helper-text">
                        MNP予約番号の有効期限は10日以上残っていることが必要です。新たな予約番号を取得後再度お申込みください
                      </p>
                    {/if}
                  </div>
                </div>
              </div>
            {/if}

            <!-- 氏名 -->
            <div class="flex-1 justify-start mt-4">
              <label for="sei" class="form-input-label required">氏名</label>
              <div class="flex flex-row justify-start space-x-4">
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="sei"
                    maxlength="50"
                    autocomplete="off"
                    placeholder="姓"
                    bind:value={$sei.value}
                  />
                  {#if $myForm.hasError('sei.required')}
                    <p class="helper-text">姓を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('sei.not_zenkaku')}
                    <p class="helper-text">姓は全角50文字以内で入力ください</p>
                  {/if}
                </div>
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="mei"
                    maxlength="50"
                    autocomplete="off"
                    placeholder="名"
                    bind:value={$mei.value}
                  />
                  {#if $myForm.hasError('mei.required')}
                    <p class="helper-text">名を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('mei.not_zenkaku')}
                    <p class="helper-text">名は全角50文字以内で入力ください</p>
                  {/if}
                </div>
              </div>
              <div class="flex flex-row justify-start space-x-4">
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="seiKana"
                    maxlength="50"
                    autocomplete="off"
                    placeholder="姓（カタカナ）"
                    bind:value={$seiKana.value}
                  />
                  {#if $myForm.hasError('seiKana.required')}
                    <p class="helper-text">姓(カナ)を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('seiKana.not_zenkaku_kana')}
                    <p class="helper-text">姓(カナ)は全角カナ50文字以内で入力ください</p>
                  {/if}
                </div>
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="meiKana"
                    maxlength="50"
                    autocomplete="off"
                    placeholder="名（カタカナ）"
                    bind:value={$meiKana.value}
                  />
                  {#if $myForm.hasError('meiKana.required')}
                    <p class="helper-text">名(カナ)を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('meiKana.not_zenkaku_kana')}
                    <p class="helper-text">名(カナ)は全角カナ50文字以内で入力ください</p>
                  {/if}
                </div>
              </div>
            </div>

            <!-- 性別 -->
            <div class="flex-1 justify-start mt-4">
              <label for="sex" class="form-input-label required">性別</label>
              <div class="flex flex-wrap space-x-6 mt-2">
                <label class="flex items-center">
                  <input type="radio" name="sex" value="MALE" checked={$sex.value === 'MALE'} bind:group={$sex.value} />
                  <span class="text-sm ml-1">男性</span>
                </label>
                <label class="flex items-center">
                  <input
                    type="radio"
                    name="sex"
                    value="FEMALE"
                    checked={$sex.value === 'FEMALE'}
                    bind:group={$sex.value}
                  />
                  <span class="text-sm ml-1">女性</span>
                </label>
              </div>
              {#if $myForm.hasError('sex.required')}
                <p class="helper-text">性別を選択してください</p>
              {/if}
            </div>

            <!-- 生年月日 -->
            <div class="flex-1 justify-start mt-4">
              <label for="birthDate" class="form-input-label required">生年月日</label>
              <div class="flex flex-row justify-start space-x-4">
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="birthDate"
                    maxlength="8"
                    autocomplete="off"
                    placeholder="YYYYMMDD"
                    bind:value={$birthDate.value}
                  />
                  {#if $myForm.hasError('birthDate.required')}
                    <p class="helper-text">生年月日を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('birthDate.invalid_date')}
                    <p class="helper-text">生年月日を正しく入力してください</p>
                  {/if}
                </div>
                <div class="basis-1/2"></div>
              </div>
            </div>

            <!-- 住所 -->
            <div class="flex-1 justify-start mt-4">
              <label for="sei" class="form-input-label required">住所</label>
              <div class="flex flex-row justify-start space-x-4">
                <div class="basis-1/2">
                  <input
                    type="text"
                    id="zipcode"
                    maxlength="7"
                    autocomplete="off"
                    placeholder="郵便番号（ハイフンなし）"
                    bind:value={$zipcode.value}
                  />
                  {#if $myForm.hasError('zipcode.required')}
                    <p class="helper-text">郵便番号を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('zipcode.invalid_zipcode')}
                    <p class="helper-text">郵便番号が間違えています。正しい郵便番号を入力ください</p>
                  {/if}
                </div>
                <div class="basis-1/2"></div>
              </div>
              <div class="flex flex-row justify-start space-x-4">
                <div class="basis-1/2">
                  <select
                    id="prefectureCode"
                    class="pl-2 pr-3 pt-3 pb-3 mt-1 has-[option:disabled:checked]:text-gray-400"
                    bind:value={$prefectureCode.value}
                  >
                    <option value="" disabled selected>選択してください（都道府県）</option>
                    {#each prefectures as prefecture}
                      <option value={prefecture.id} selected={prefecture.id === $prefectureCode.value}
                        >{prefecture.name}</option
                      >
                    {/each}
                  </select>
                  {#if $myForm.hasError('prefectureCode.required')}
                    <p class="helper-text">都道府県を選択してください</p>
                  {/if}
                </div>

                <div class="basis-1/2"></div>
              </div>
              <div class="flex flex-row justify-start space-x-4">
                <div class="flex-1">
                  <input
                    type="text"
                    id="city"
                    autocomplete="off"
                    maxlength="100"
                    placeholder="市区町村"
                    bind:value={$city.value}
                  />
                  {#if $myForm.hasError('city.required')}
                    <p class="helper-text">市区町村を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('city.not_zenkaku')}
                    <p class="helper-text">市区町村は全角100文字以内で入力ください</p>
                  {/if}
                </div>
              </div>
              <div class="flex flex-row justify-start space-x-4">
                <div class="flex-1">
                  <input
                    type="text"
                    id="street"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="番地号建物名"
                    bind:value={$street.value}
                  />
                  {#if $myForm.hasError('street.required')}
                    <p class="helper-text">番地号建物名を入力してください</p>
                  {/if}
                  {#if $myForm.hasError('street.not_zenkaku')}
                    <p class="helper-text">番地号建物名は全角100文字以内で入力ください</p>
                  {/if}
                </div>
              </div>
              <div class="flex flex-row justify-start space-x-4">
                <div class="flex-1">
                  <input
                    type="text"
                    id="building"
                    maxlength="100"
                    autocomplete="off"
                    placeholder="部屋番号（任意）"
                    bind:value={$building.value}
                  />
                  {#if $myForm.hasError('building.not_zenkaku')}
                    <p class="helper-text">部屋番号は全角100文字以内で入力ください</p>
                  {/if}
                </div>
              </div>
              <!-- 連絡先電話番号 -->
              <div class="flex-1 justify-start mt-4">
                <label for="telno" class="form-input-label required">連絡先電話番号（ハイフンなし）</label>
                <div class="flex flex-row justify-start space-x-4">
                  <div class="basis-1/2">
                    <input
                      type="text"
                      id="telno"
                      maxlength="11"
                      autocomplete="off"
                      placeholder="例) 08011112222"
                      bind:value={$telno.value}
                    />
                    {#if $myForm.hasError('telno.required')}
                      <p class="helper-text">連絡先電話番号を入力してください</p>
                    {/if}
                    {#if $myForm.hasError('telno.invalid_tel_no')}
                      <p class="helper-text">連絡先電話番号が不正です。正しい電話番号を入力ください</p>
                    {/if}
                  </div>
                  <div class="basis-1/2"></div>
                </div>
              </div>

              <!-- メールアドレス -->
              <div class="flex-1 justify-start mt-4">
                <label for="mailAddress" class="form-input-label">メールアドレス</label>
                <div class="flex flex-row justify-start space-x-4">
                  <div class="basis-1/2" inert={true}>
                    <input
                      type="text"
                      id="mailAddress"
                      autocomplete="off"
                      value={verifyAccessTokenResult.email}
                      readonly
                    />
                  </div>
                  <div class="basis-1/2"></div>
                </div>
              </div>
              <!-- お支払方法 -->
              <div class="flex-1 justify-start mt-4">
                <label for="paytypeName" class="form-input-label">支払方法</label>
                <div class="flex flex-row justify-start space-x-4">
                  <div class="basis-1/2" inert={true}>
                    <input type="text" id="paytypeName" autocomplete="off" value="クレジットカード" readonly />
                  </div>
                  <div class="basis-1/2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
      <div class="flex justify-center mt-10 mb-10">
        <button class="tracking-widest form-button h-12 w-1/4" on:click={handleConfirm}>次へ</button>
      </div>
    </main>

    <footer class="container" />
  </div>
{:else if display === 'CONFIRM'}
  <div class="flex flex-col justify-between items-center login-background">
    <header class="container top-0 z-0">
      <div class="justify-between items-center" style="height:56px">
        <img style="height:36px" class="object-none w-full" src="/image/unext_communication_logo.svg" alt="logo" />
      </div>
    </header>

    <main class="container mt-10 mb-auto">
      <div class="flex justify-center text-2xl">U-mobile(d)移管特別オファー申込ページ</div>

      <div class="flex justify-center mt-10">
        <div class="content-center shadow-sm p-4 form-background">
          <div class="flex flex-col">
            <h4 class="text-base md:text-lg font-semibold text-gray-700 text-start py-2">お申込み情報</h4>

            <div class="flex flex-col justify-center items-center divide-y-2 devide-brack-500">
              <!-- divide用の空div -->
              <div class="min-w-full" />

              <!-- 招待コード -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">招待コード</span>
                </div>
                <div class="basis-3/5 justify-self-start w-full">
                  <p class="label-value">{verifyInvitationCodeResult?.invitationCode}</p>
                </div>
              </div>

              <!-- 申込プラン -->
              <div class="inline-flex items-center min-w-full p-4 mb:p-2">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">申込プラン</span>
                </div>
                <div class="basis-3/5 justify-self-start w-full">
                  <p class="label-value">{getPlanCodeLabel(verifyInvitationCodeResult?.planTypeCode, plancode)}</p>
                </div>
              </div>

              {#if verifyInvitationCodeResult?.planTypeCode === 'VOICE'}
                <!-- MNP電話番号 -->
                <div class="inline-flex items-center min-w-full p-4">
                  <div class="basis-2/5 justify-self-start w-full">
                    <span class="label-title">MNP電話番号</span>
                  </div>
                  <div class="basis-3/5 justify-self-start w-full">
                    <p class="label-value">{$mnpTelno.value}</p>
                  </div>
                </div>

                <!-- MNP予約番号 / 有効期限 -->
                <div class="inline-flex items-center min-w-full p-4">
                  <div class="basis-2/5 justify-self-start w-full">
                    <span class="label-title">MNP予約番号 / 有効期限</span>
                  </div>
                  <div class="basis-3/5 justify-self-start w-full">
                    <p class="label-value">{`${$mnpReserveNo.value} / ${$mnpExpiration.value}`}</p>
                  </div>
                </div>
              {/if}

              <!-- 氏名 -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start self-start w-full">
                  <span class="label-title">氏名</span>
                </div>
                <div class="basis-3/5 justify-self-start w-full">
                  <ul class="list-none">
                    <li>
                      <p class="label-value">{`${$sei.value}${$mei.value}`}</p>
                    </li>
                    <li>
                      <p class="label-value">{`${$seiKana.value}${$meiKana.value}`}</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- 性別 -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">性別</span>
                </div>
                <div class="basis-3/5 justify-self-start w-full">
                  <ul class="list-none">
                    <li>
                      <p class="label-value">{getSexName($sex.value)}</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- 生年月日 -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">生年月日</span>
                </div>
                <div class="basis-3/5 col-span-2 justify-self-start w-full">
                  <ul class="list-none">
                    <li>
                      <p class="label-value">{formatBirthDate($birthDate.value)}</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- 住所 -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start self-start w-full">
                  <span class="label-title">住所</span>
                </div>
                <div class="basis-3/5 col-span-2 justify-self-start w-full">
                  <ul class="list-none">
                    <li class="label-value">{$zipcode.value}</li>
                    <li>
                      <p class="label-value">{getPrefectureName($prefectureCode.value)}</p>
                    </li>
                    <li>
                      <p class="label-value">{$city.value}</p>
                    </li>
                    <li>
                      <p class="label-value">{$street.value}</p>
                    </li>
                    <li>
                      <p class="label-value">{$building.value}</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- 連絡先電話番号 -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">連絡先電話番号</span>
                </div>
                <div class="basis-3/5 col-span-2 justify-self-start w-full">
                  <ul class="list-none">
                    <li>
                      <p class="label-value">{$telno.value}</p>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- メールアドレス -->
              <div class="inline-flex items-center min-w-full p-4">
                <div class="basis-2/5 justify-self-start w-full">
                  <span class="label-title">メールアドレス</span>
                </div>
                <div class="basis-3/5 col-span-2 justify-self-start w-full">
                  <ul class="list-none">
                    <li>
                      <p class="label-value">{verifyAccessTokenResult?.email}</p>
                    </li>
                  </ul>
                </div>
              </div>
              <!-- divide用の空div -->
              <div class="min-w-full" />
            </div>
            <div class="flex flex-col mt-10">
              <h4 class="text-base md:text-lg font-semibold text-gray-700 text-start py-2">お支払方法</h4>

              <div class="flex flex-col justify-center items-center divide-y-2 devide-brack-500">
                <!-- divide用の空div -->
                <div class="min-w-full" />

                <!-- お支払方法 -->
                <div class="inline-flex items-center min-w-full p-4">
                  <div class="basis-2/5 justify-self-start w-full">
                    <span class="label-title">お支払方法</span>
                  </div>

                  <div class="basis-3/5 justify-self-start w-full">
                    <ul class="list-none">
                      <li>
                        <p class="label-value">クレジットカード</p>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- お支払時期 -->
                <div class="inline-flex items-center min-w-full p-4">
                  <div class="basis-2/5 justify-self-start w-full">
                    <span class="label-title">お支払時期</span>
                  </div>

                  <div class="basis-3/5 justify-self-start w-full">
                    <ul class="list-none">
                      <li>
                        <p class="label-value">クレジットカード会社が定める引き落とし日</p>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- divide用の空div -->
                <div class="min-w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col justify-items-center items-center my-5">
        <div class="flex flex-col justify-items-center items-center min-w-full">
          <ul class="list-none list-inside">
            {#if applyErrroMessage}
              <li><span class="helper-text">{applyErrroMessage}</span></li>
            {/if}
          </ul>
        </div>
      </div>

      <div class="flex flex-col justify-items-center items-center my-10">
        <div class="flex flex-col justify-items-center items-center min-w-full">
          <button class="tracking-widest form-button mb-5 h-12 md:w-1/4 w-3/4" on:click={handleApply}
            >この内容で申込</button
          >
          <button class="tracking-widest text-center text-sm h-auto w-auto" on:click={handleReturnToInput}
            >申込情報入力へ戻る</button
          >
        </div>
      </div>

      <div class="flex justify-center mx-10"></div>
    </main>

    <footer class="container" />
  </div>
{:else}
  <NotFound />
{/if}

<style lang="postcss">
  .required:after {
    content: ' *';
    color: red;
  }

  .login-background {
    background-color: white;
  }

  .content-help-title-xl {
    @apply text-xl font-semibold text-gray-900;
  }

  .content-help-title {
    @apply text-lg font-semibold text-gray-900;
  }

  .content-help-text {
    @apply text-sm font-normal text-gray-900;
  }

  .form-input-label {
    @apply text-base font-normal text-gray-900;
  }
  .label-title {
    @apply md:text-base text-sm font-medium text-gray-700;
  }
  .label-value {
    @apply md:text-base text-sm font-normal text-gray-900;
  }

  select {
    font-size: 14px;
    color: rgba(0, 10, 23, 1);
    background-color: rgba(1, 6, 13, 0.06);
    width: 100%;
    @apply min-h-12 focus:ring-0 focus:ring-blue-200 focus:border-transparent;
  }

  input[type='text'] {
    font-size: 14px;
    color: rgba(0, 10, 23, 1);
    background-color: rgba(1, 6, 13, 0.06);
    @apply p-3 mt-1 appearance-none min-h-12 w-full focus:ring-0 focus:ring-blue-200 focus:border-transparent;
  }

  .form-background {
    background-color: rgba(0, 0, 0, 0.02);
    @apply w-9/12;
  }

  .warning-background {
    background-color: rgba(0, 0, 0, 0.12);
    @apply w-10/12;
  }

  @media screen and (max-width: 640px) {
    .form-background {
      width: 100%;
    }
    .warning-background {
      width: 100%;
    }
  }

  .form-button {
    @apply text-center text-sm font-medium rounded-none text-white bg-[#0fb9f5] focus:enabled:outline-none;
  }
  .form-button:hover:enabled {
    text-decoration: none;
    -webkit-filter: brightness(1.1);
    filter: brightness(1.1);
  }

  .form-button:disabled {
    @apply opacity-30 cursor-default;
  }

  .helper-text {
    @apply text-xs text-red-500  mt-2;
  }
</style>
